import { baseApi } from 'core/api';
import { useToastStore } from 'store/modules';

const state = () => ({
    _teams: [],
    _pagination: null,
    _parameters: {
        sortBy: 'created_at',
        sortOrder: 'desc',
    },
    _loading: false,
});

const getters = {
    teams: (state) => state._teams,
    teamsLoading: (state) => state._loading,
    teamsParameters: (state) => state._parameters,
    teamsPagination: (state) => state._pagination,
};

const actions = {
    getGroups({ retry = false, search = '', length = 10, page = 1 } = {}) {
        if (this._loading) return;

        this._setTeamsLoading(true);

        if (retry) {
            this._setTeamsLoadRetry();
        }

        return baseApi
            .get('core/groups', {
                params: {
                    search,
                    length,
                    page,
                },
            })
            .then((response) => {
                this._setTeamsLoaded(response.data);
                return response;
            })
            .catch((error) => {
                this._setTeamsLoadError();
                return error;
            })
            .finally(() => {
                this._setTeamsLoading(false);
            });
    },

    createGroupsTeam({ isCloneFlow = false, members, name, description }) {
        if (this._loading) return;

        this._setTeamsLoading(true);

        return baseApi
            .post('core/groups', {
                members,
                name,
                description,
            })
            .then((response) => {
                return response;
            })
            .catch((error) => {
                useToastStore().showToast({
                    type: 'danger',
                    message: `Failed to ${isCloneFlow ? 'clone' : 'create'} team!`,
                });

                return error?.response;
            })
            .finally(() => {
                this._setTeamsLoading(false);
            });
    },

    createGroupsIntegrationTeam({ isCloneFlow = false, integration_team_id }) {
        if (this._loading) return;

        this._setTeamsLoading(true);

        return baseApi
            .post('core/groups/create-integration-group', {
                integration_team_id,
            })
            .then((response) => {
                return response;
            })
            .catch((error) => {
                useToastStore().showToast({
                    type: 'danger',
                    message: `Failed to ${isCloneFlow ? 'clone' : 'create'} team!`,
                });

                return error?.response;
            })
            .finally(() => {
                this._setTeamsLoading(false);
            });
    },

    updateGroupById({ group_id, members, name, description }) {
        if (this._loading) return;

        this._setTeamsLoading(true);

        return baseApi
            .put('core/groups/update', {
                group_id,
                members,
                name,
                description,
            })
            .then((response) => {
                return response;
            })
            .catch((error) => {
                useToastStore().showToast({
                    type: 'danger',
                    message: 'Failed to update team!',
                });

                return error?.response;
            })
            .finally(() => {
                this._setTeamsLoading(false);
            });
    },

    deleteGroupById({ group_id }) {
        if (this._loading) return;

        this._setTeamsLoading(true);

        return baseApi
            .delete(`core/groups/${group_id}`)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                useToastStore().showToast({
                    type: 'danger',
                    message: 'Failed to delete team!',
                });

                return error?.response;
            })
            .finally(() => {
                this._setTeamsLoading(false);
            });
    },

    teamsSortUpdate(sort) {
        let teams = [...this._teams];

        const sortData = (sortBy, sortOrder) => {
            if (sortOrder === 'asc') return (a, b) => (a[sortBy] > b[sortBy] ? 1 : -1);
            return (a, b) => (a[sortBy] < b[sortBy] ? 1 : -1);
        };

        teams.sort(sortData(sort.sortBy, sort.sortOrder));

        this._setMemberSortUpdate(sort);
        this._setMembersLoaded(teams);
    },

    refreshIntegrationTeamContactsById({ id }) {
        return baseApi
            .get(`core/groups/refresh-integration-team-contacts/${id}`)
            .then((response) => {
                return response;
            });
    },

    _setTeamsLoading(status) {
        this._loading = status;
    },

    _setTeamsLoadRetry() {
        this._teams = null;
    },

    _setTeamsLoadError() {
        this._teams = false;
    },

    _setTeamsLoaded(data) {
        this._teams = data.data;
        this._pagination = {
            current_page: data.meta?.current_page,
            from: data.meta?.from,
            last_page: data.meta?.last_page,
            per_page: data.meta?.per_page,
            to: data.meta?.to,
            total: data.meta?.total,
        };
    },

    _setTeamsRemoved(id) {
        this._teams = this._teams.filter((o) => o.id !== id);
    },

    _setTeamsSortUpdate(data) {
        this._parameters.sortBy = data.sortBy;
        this._parameters.sortOrder = data.sortOrder;
    },

    // Cross-store method calls (these might be wrong types)
    _setMemberSortUpdate(sort) {
        // This seems like it should be _setTeamsSortUpdate
        this._parameters.sortBy = sort.sortBy;
        this._parameters.sortOrder = sort.sortOrder;
    },

    _setMembersLoaded(teams) {
        // This seems like it should be _setTeamsLoaded but with different data structure
        this._teams = teams;
    },
};

export default {
    state,
    getters,
    actions,
};
